import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Trash2, Loader2, CreditCard, Banknote, Smartphone, Check, Truck, Tag, CalendarCheck, ArrowLeft } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { useCart } from "../context/CartContext";
import { createCheckout, createReservation, lookupAddress, getDeliveryQuote, validateDiscount, fileUrl } from "../api";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";
import { useSeo } from "../hooks/useSeo";

const methods = [
  { key: "ideal", label: "iDEAL", desc: "Direct online betalen", icon: Smartphone },
  { key: "pinnen", label: "Pinnen op locatie", desc: "Pin bij levering (SumUp)", icon: CreditCard },
  { key: "contant", label: "Contant", desc: "Betaal bij levering", icon: Banknote },
];

const eur = (v) => `€${Number(v).toFixed(2).replace(".", ",")}`;

const Cart = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { items, removeItem, clear, subtotal, lineTotal } = useCart();
  const [method, setMethod] = useState("ideal");
  const [form, setForm] = useState({ name: "", email: "", phone: "", postcode: "", huisnummer: "", straat: "", plaats: "" });
  const [delivery, setDelivery] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [checkingCode, setCheckingCode] = useState(false);

  useSeo({ title: "Winkelwagen | Jump en Joy", description: "Bekijk je gekozen springkussens, vul je adres in en reken veilig af. Bezorgkosten worden één keer berekend voor je hele bestelling." });

  const deliveryCost = delivery ? delivery.delivery_cost : 0;
  const discountAmount = discount && discount.valid ? Math.round(subtotal * discount.percent) / 100 : 0;
  const total = Math.max(0, subtotal - discountAmount) + deliveryCost;

  const address = [form.straat, form.huisnummer].filter(Boolean).join(" ") + (form.postcode || form.plaats ? `, ${form.postcode} ${form.plaats}` : "");

  const fetchQuote = async (postcode, huisnummer) => {
    if (!postcode || !huisnummer) return;
    setQuoting(true);
    try {
      const q = await getDeliveryQuote(postcode, huisnummer);
      setDelivery(q && q.resolved ? q : null);
    } catch { setDelivery(null); }
    finally { setQuoting(false); }
  };

  const onAddressBlur = async () => {
    if (!form.postcode || !form.huisnummer) return;
    try {
      const res = await lookupAddress(form.postcode, form.huisnummer);
      if (res) setForm((f) => ({ ...f, straat: res.straat, plaats: res.plaats }));
    } catch { /* graceful */ }
    fetchQuote(form.postcode, form.huisnummer);
  };

  const applyCode = async () => {
    if (!code.trim()) return;
    setCheckingCode(true);
    try {
      const res = await validateDiscount(code.trim());
      if (res.valid) { setDiscount(res); toast({ title: `Kortingscode toegepast: ${res.percent}% korting` }); }
      else { setDiscount(null); toast({ title: "Ongeldige code", description: res.reason || "Deze kortingscode is niet geldig." }); }
    } catch { toast({ title: "Kon code niet controleren" }); }
    finally { setCheckingCode(false); }
  };

  const normalizePostcode = (v) => v.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 6);
  const postcodeValid = /^[1-9][0-9]{3}[A-Z]{2}$/.test(form.postcode);
  const huisnummerValid = /^[0-9]+$/.test(form.huisnummer);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const phoneValid = form.phone.replace(/[^0-9]/g, "").length >= 8;

  const submit = async () => {
    if (!form.name.trim()) { toast({ title: "Vul je naam in" }); return; }
    if (!emailValid) { toast({ title: "Ongeldig e-mailadres", description: "Vul een geldig e-mailadres in." }); return; }
    if (!phoneValid) { toast({ title: "Ongeldig telefoonnummer", description: "Vul een geldig telefoonnummer in." }); return; }
    if (!postcodeValid) { toast({ title: "Ongeldige postcode", description: "Gebruik het formaat 1234 AB." }); return; }
    if (!huisnummerValid) { toast({ title: "Ongeldig huisnummer", description: "Vul een huisnummer in (alleen cijfers)." }); return; }
    if (quoting) { toast({ title: "Bezorgkosten worden berekend", description: "Een moment geduld." }); return; }
    if (!delivery) { toast({ title: "Adres niet gevonden", description: "We konden je adres niet vinden. Controleer je postcode en huisnummer." }); fetchQuote(form.postcode, form.huisnummer); return; }
    setLoading(true);
    const payload = {
      items: items.map((i) => ({ product_slug: i.slug, start_date: i.startDate, end_date: i.endDate, ondergrond: i.ondergrond || "", extra_vullingen: Number(i.extraVullingen) || 0, flavor: i.flavor || "" })),
      name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), address,
      postcode: form.postcode, huisnummer: form.huisnummer,
      discount_code: discount && discount.valid ? discount.code : "",
    };
    try {
      if (method === "ideal") {
        const { url } = await createCheckout({ ...payload, origin_url: window.location.origin });
        window.location.href = url;
      } else {
        await createReservation({ ...payload, method });
        clear();
        setLoading(false);
        toast({ title: "Reservering bevestigd!", description: method === "pinnen" ? "Je pint bij levering op locatie. Bevestiging volgt per e-mail." : "Je betaalt contant bij levering. Bevestiging volgt per e-mail." });
        navigate("/betaling/succes");
      }
    } catch (e) {
      setLoading(false);
      toast({ title: "Er ging iets mis", description: e?.response?.data?.detail || "Probeer het opnieuw." });
    }
  };

  if (items.length === 0) {
    return (
      <div className="wrap py-20 text-center">
        <div className="w-20 h-20 rounded-full bg-brand-bluelight mx-auto flex items-center justify-center"><ShoppingCart className="w-9 h-9 text-brand-blue" /></div>
        <h1 className="font-display font-bold text-brand-navy text-3xl mt-6">Je winkelwagen is leeg</h1>
        <p className="text-slate-500 mt-3">Voeg een of meerdere springkussens toe en reken in één keer af.</p>
        <Link to="/onze-luchtkastelen" data-testid="cart-empty-shop-link" className="inline-flex mt-8 rounded-full bg-brand-blue hover:bg-brand-bluedark text-white font-bold px-7 py-3.5 transition-colors">Bekijk het aanbod</Link>
      </div>
    );
  }

  return (
    <div className="wrap py-12">
      <Link to="/onze-luchtkastelen" className="inline-flex items-center gap-2 text-brand-blue font-semibold hover:gap-3 transition-all"><ArrowLeft className="w-4 h-4" /> Verder winkelen</Link>
      <h1 className="font-display font-bold text-brand-navy text-3xl sm:text-4xl mt-4">Winkelwagen</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8 items-start">
        {/* Items + form */}
        <div className="lg:col-span-2 space-y-4">
          <div className="space-y-3" data-testid="cart-items">
            {items.map((i) => (
              <div key={`${i.slug}-${i.startDate}-${i.endDate}`} data-testid={`cart-item-${i.slug}`} className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-3 shadow-sm">
                <img src={fileUrl(i.image)} alt={i.name} className="w-20 h-20 rounded-xl object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-display text-brand-navy">{i.name}</p>
                  <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5">
                    <CalendarCheck className="w-4 h-4 text-brand-blue" />
                    {i.days > 1
                      ? `${format(parseISO(i.startDate), "d MMM", { locale: nl })} t/m ${format(parseISO(i.endDate), "d MMM yyyy", { locale: nl })}`
                      : format(parseISO(i.startDate), "EEEE d MMMM yyyy", { locale: nl })}
                  </p>
                  {i.days > 1 && <p className="text-xs text-brand-blue font-semibold mt-0.5">{i.days} dagen × {eur(i.price)}</p>}
                  {i.flavor && <p className="text-xs text-slate-500 mt-0.5">Smaak: {i.flavor}</p>}
                  {i.extraVullingen > 0 && <p className="text-xs text-brand-green font-semibold mt-0.5">+ {i.extraVullingen} × 100 extra vullingen ({eur(i.extraVullingen * 10)})</p>}
                  {i.ondergrond && <p className="text-xs text-slate-400 mt-0.5">Ondergrond: {i.ondergrond}</p>}
                </div>
                <span className="font-display font-bold text-brand-navy whitespace-nowrap">{eur(lineTotal(i))}</span>
                <button onClick={() => removeItem(i.slug, i.startDate, i.endDate)} data-testid={`cart-remove-${i.slug}`} aria-label="Verwijderen" className="text-brand-pink hover:text-brand-pinkdark p-2"><Trash2 className="w-5 h-5" /></button>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-100 bg-white p-5 sm:p-6 space-y-4">
            <h2 className="font-display text-brand-navy text-xl">Jouw gegevens</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="Naam *" required data-testid="cart-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input type="email" placeholder="E-mail *" required data-testid="cart-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input type="tel" placeholder="Telefoon *" required data-testid="cart-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <div />
              <div>
                <Input placeholder="Postcode * (1234 AB)" required maxLength={7} data-testid="cart-postcode" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: normalizePostcode(e.target.value), straat: "", plaats: "" })} onBlur={onAddressBlur} className={form.postcode && !postcodeValid ? "border-brand-pink" : ""} />
                {form.postcode && !postcodeValid && <p className="text-[11px] text-brand-pink mt-1">Formaat: 1234 AB</p>}
              </div>
              <div>
                <Input placeholder="Huisnr. *" required inputMode="numeric" data-testid="cart-huisnummer" value={form.huisnummer} onChange={(e) => setForm({ ...form, huisnummer: e.target.value.replace(/[^0-9]/g, ""), straat: "", plaats: "" })} onBlur={onAddressBlur} className={form.huisnummer && !huisnummerValid ? "border-brand-pink" : ""} />
              </div>
            </div>
            {(form.straat || form.plaats) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input placeholder="Straat" value={form.straat} onChange={(e) => setForm({ ...form, straat: e.target.value })} />
                <Input placeholder="Plaats" value={form.plaats} onChange={(e) => setForm({ ...form, plaats: e.target.value })} />
              </div>
            )}
            {form.straat && <p className="text-xs text-brand-green font-semibold flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {address}</p>}
          </div>

          <div className="rounded-2xl border border-slate-100 bg-white p-5 sm:p-6">
            <h2 className="font-display text-brand-navy text-xl mb-3">Betaalmethode</h2>
            <div className="grid grid-cols-3 gap-2">
              {methods.map((m) => {
                const Icon = m.icon; const sel = method === m.key;
                return (
                  <button key={m.key} type="button" onClick={() => setMethod(m.key)} data-testid={`cart-method-${m.key}`}
                    className={`relative rounded-2xl border-2 p-3 text-center transition-all ${sel ? "border-brand-blue bg-brand-bluelight" : "border-slate-200 hover:border-brand-blue/50"}`}>
                    {sel && <Check className="w-4 h-4 text-white bg-brand-blue rounded-full p-0.5 absolute top-1.5 right-1.5" />}
                    <Icon className={`w-6 h-6 mx-auto ${sel ? "text-brand-blue" : "text-slate-400"}`} />
                    <span className="block font-display text-sm text-brand-navy mt-1.5">{m.label}</span>
                    <span className="block text-[10px] text-slate-400 leading-tight mt-0.5">{m.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm lg:sticky lg:top-24" data-testid="cart-summary">
          <h2 className="font-display text-brand-navy text-xl">Overzicht</h2>

          <div className="mt-4">
            <Label className="text-sm font-semibold text-brand-navy">Kortingscode</Label>
            <div className="flex gap-2 mt-1.5">
              <Input placeholder="Bijv. ZOMER10" data-testid="cart-discount-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="uppercase" />
              <button onClick={applyCode} disabled={checkingCode} data-testid="cart-apply-discount" className="rounded-xl bg-brand-navy hover:bg-brand-navysoft text-white font-bold px-4 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60">
                {checkingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />} Toepassen
              </button>
            </div>
            {discount && discount.valid && <p className="text-xs text-brand-green font-semibold mt-1.5 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {discount.code} · {discount.percent}% korting op de dagprijzen</p>}
          </div>

          <div className="mt-5 space-y-2.5 text-sm">
            <div className="flex justify-between text-brand-navy"><span>Subtotaal ({items.length} {items.length === 1 ? "attractie" : "attracties"})</span><span className="font-semibold" data-testid="cart-subtotal">{eur(subtotal)}</span></div>
            {discountAmount > 0 && <div className="flex justify-between text-brand-pink"><span>Korting</span><span className="font-semibold" data-testid="cart-discount-amount">-{eur(discountAmount)}</span></div>}
            <div className="flex justify-between text-brand-navy">
              <span className="inline-flex items-center gap-1.5"><Truck className="w-4 h-4 text-brand-blue" /> Bezorgkosten</span>
              <span className="font-semibold" data-testid="cart-delivery-cost">
                {quoting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : !delivery ? <span className="text-slate-400 font-normal">Vul adres in</span> : deliveryCost === 0 ? <span className="text-brand-green">Gratis</span> : eur(deliveryCost)}
              </span>
            </div>
            {delivery && delivery.distance_km != null && (
              <p className="text-[11px] text-slate-400">{delivery.distance_km.toString().replace(".", ",")} km enkele reis vanaf {delivery.origin_label}{deliveryCost === 0 ? ` · gratis binnen ${delivery.free_radius} km` : ` · €${Number(delivery.per_km).toFixed(2).replace(".", ",")}/km`}</p>
            )}
            <div className="flex justify-between pt-3 border-t border-slate-200 items-center">
              <span className="font-display text-brand-navy">Totaal</span>
              <span className="font-display font-bold text-brand-blue text-xl" data-testid="cart-total">{eur(total)}</span>
            </div>
          </div>

          <button onClick={submit} disabled={loading} data-testid="cart-checkout-button" className="w-full mt-5 rounded-full bg-brand-pink hover:bg-brand-pinkdark text-white font-bold py-3.5 transition-colors shadow-md flex items-center justify-center gap-2 disabled:opacity-70">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Bezig..." : method === "ideal" ? `Betaal ${eur(total)} via iDEAL` : `Reserveer · ${eur(total)}`}
          </button>
          <p className="text-xs text-slate-400 text-center mt-3">Levering, opbouw en ophalen inbegrepen. Bezorgkosten worden één keer berekend.</p>
        </div>
      </div>
    </div>
  );
};

export default Cart;
